import { Link, useRouterState } from '@tanstack/react-router'
import type { HTMLAttributes, ReactNode } from 'react'

import { cn } from '@/lib/utils'

export type ExternalNavItem = {
  to: string
  label: string
}

export const defaultExternalNavItems: ExternalNavItem[] = [
  { to: '/membership/apply', label: 'Membership application' },
  { to: '/login', label: 'Log in' },
]

interface ExternalPageHeaderProps extends HTMLAttributes<HTMLElement> {
  navItems?: ExternalNavItem[]
  actions?: ReactNode
  isAuthenticated?: boolean
}

export function ExternalPageHeader({
  navItems = defaultExternalNavItems,
  actions,
  isAuthenticated,
  className,
  ...rest
}: ExternalPageHeaderProps) {
  const { location } = useRouterState()
  const pathname = location.pathname ?? '/'

  return (
    <header
      className={cn(
        'sticky top-0 z-20 w-full rounded-b-2xl border-b border-white/30 bg-white/40 shadow-lg backdrop-blur-xl supports-[backdrop-filter]:bg-white/25 dark:border-slate-800/60 dark:bg-slate-950/60 dark:supports-[backdrop-filter]:bg-slate-950/45',
        className,
      )}
      {...rest}
    >
      <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center gap-x-6 gap-y-3 px-4 py-2">
        <Link
          to="/"
          preload="intent"
          className="text-base font-semibold text-foreground transition-colors hover:text-primary"
        >
          <div className="flex items-left justify-end p-2">
            <img src="/images/logo/logo.png" alt="UIBFS logo" className="block dark:hidden object-contain" width={45} height={10} />
            <img src="/images/logo/logo-light.png" alt="UIBFS logo" className="hidden dark:block object-contain" width={40} height="auto" />
            <span className="ml-2 text-sm sm:text-2xl font-bold text-primary dark:text-foreground">Membership Management System</span>
          </div>

        </Link>
        <nav className="flex flex-1 flex-wrap items-center justify-start gap-x-4 gap-y-2 text-sm sm:justify-end">
          {navItems
            .filter((item) => (isAuthenticated ? item.to !== '/login' && item.to !== '/signup' : true))
            .map(({ to, label }) => {
              const isActive =
                pathname === to || (to !== '/' && pathname.startsWith(`${to}/`))

              return (
                <Link
                  key={to}
                  to={to}
                  preload="intent"
                  className={cn(
                    'font-medium text-slate-700/90 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 dark:text-slate-200/90 dark:hover:text-primary',
                    isActive ? 'font-semibold text-primary border-primary dark:text-primary border-b-2 ' : undefined,
                  )}
                >
                  {label}

                </Link>
              )
            })}
        </nav>
        {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
      </div>
    </header>
  )
}
